import currency from "currency.js";
import { pickBy } from "lodash";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import React, { useEffect, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import { Link } from "react-router-dom";
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear } from "date-fns";
import CachedIcon from "mdi-react/CachedIcon";
import * as XLSX from "xlsx";
import { ExcelIcon } from "../../Icons";
import { useRef } from "react";

const monthName = i => new Date(0, i).toLocaleString("default", { month: "long" });

const BudgetVsActualModal = ({
  show,
  accounts = [],
  budget,
  onHide,
  title = "Budget vs Actual",
}) => {
  const { backendUrl } = useAuth();
  const [isOpen, setOpen] = useState({});

  const tableRef = useRef(null);

  const exportToExcel = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });

    // Create a safe filename for all OSes by replacing invalid characters with underscores
    const rawFilename = `${budget.budgetType}_${
      budget.period
    }_BVSA_${new Date().toISOString().slice(0, 10)}`;
    const filename = rawFilename.replace(/[^a-zA-Z0-9-_]/g, "_");

    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // get Budget by id from api
  const { data: budgetData, isLoading, error, refetch } = useQuery(
    ["budget", budget?.id],
    () => {
      return fetch(`${backendUrl}/api/budget/${budget?.id}`)
        .then(response => response.json())
        .catch(error => {
          console.error("Error fetching budget:", error);
        });
    },
    {
      enabled: !!budget?.id,
    }
  );

  const year = budget.period.match(/\d{4}/)[0];
  const startDate = format(
    startOfYear(new Date(`${year}-${String(1).padStart(2, "0")}-01`)),
    "yyyy-MM-dd"
  );
  const endDate = format(
    endOfYear(new Date(`${year}-${String(12).padStart(2, "0")}-01`)),
    "yyyy-MM-dd"
  );

  const renderNestedAccounts = (accounts, level = 0) => {
    return accounts.map(account => (
      <React.Fragment key={account.AccountID}>
        {account.isHeader ? (
          <tr>
            <td colSpan={100} className="align-middle">
              <p className="p-0">
                <Button
                  variant=""
                  onClick={() => {
                    setOpen(oldData => ({
                      ...oldData,
                      [account.name]:
                        typeof oldData[account.name] === "undefined"
                          ? false
                          : !oldData[account.name],
                    }));
                  }}
                  className="p-3 py-2 d-flex align-items-center ps-0"
                  style={{ position: "sticky", left: "1rem" }}
                >
                  <ChevronDownIcon
                    style={{
                      ...(typeof isOpen[account.name] === "undefined" || isOpen[account.name]
                        ? {}
                        : { transform: "rotate(-180deg)" }),
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <b>{account.name}</b>
                </Button>{" "}
              </p>
            </td>
          </tr>
        ) : typeof isOpen[account.name] === "undefined" || isOpen[account.name] ? (
          <tr>
            <td className="align-middle">
              <div
                style={{
                  paddingLeft: `${level * 20}px`,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <div title={account.name} className="text-truncate">
                  <Link
                    to={`/account-setup/account-list?page=1&limit=100&Description=${
                      account.name
                    }&AccountID=${
                      account.AccountID
                    }&month=All&showBank=false&currency=&convertCurrency=NGN&startDate=${startDate}&endDate=${endDate}&enableDateRange=true`}
                    target="_blank"
                  >
                    {account.name}
                  </Link>
                </div>
              </div>
            </td>

            {Array.from({ length: 12 }, (_, i) => {
              const budgetValue = budget.monthlyBudgets[`${account.AccountID}_month_${i + 1}`];
              const budgetAmount = parseFloat(budgetValue) || 0;
              const actualAmount = parseFloat(
                budgetData?.budget?.actuals[`${account.AccountID}_month_${i + 1}`] || 0
              );
              const overBudget = currency(actualAmount).subtract(budgetAmount).value;
              const percentOfBudget = budgetAmount > 0 ? (actualAmount / budgetAmount) * 100 : 0;

              const year = budget.period.match(/\d{4}/)[0];
              const startDate = format(
                startOfMonth(new Date(`${year}-${String(i + 1).padStart(2, "0")}-01`)),
                "yyyy-MM-dd"
              );
              const endDate = format(
                endOfMonth(new Date(`${year}-${String(i + 1).padStart(2, "0")}-01`)),
                "yyyy-MM-dd"
              );

              return (
                <React.Fragment key={i}>
                  <td className="align-middle" title={`${account.name} - Actual - ${monthName(i)}`}>
                    {/* ACTUAL */}
                    <Link
                      to={`/account-setup/account-list?page=1&limit=100&Description=${
                        account.name
                      }&AccountID=${
                        account.AccountID
                      }&month=All&showBank=false&currency=&convertCurrency=NGN&startDate=${startDate}&endDate=${endDate}&enableDateRange=true`}
                      target="_blank"
                    >
                      {currency(actualAmount, {
                        symbol: "",
                      }).format()}
                    </Link>
                  </td>
                  <td className="align-middle" title={`${account.name} - Budget - ${monthName(i)}`}>
                    {/* BUDGET */}
                    {Number(budgetAmount) !== 0 &&
                      currency(budgetAmount, {
                        symbol: "",
                      }).format()}
                  </td>
                  <td
                    className="align-middle"
                    title={`${account.name} - Over Budget - ${monthName(i)}`}
                  >
                    {/* OVER BUDGET */}
                    {Number(overBudget) !== 0 &&
                      currency(overBudget, {
                        symbol: "",
                      }).format()}
                  </td>
                  <td
                    className="align-middle"
                    title={`${account.name} - % of Budget - ${monthName(i)}`}
                  >
                    {/* % OF BUDGET */}
                    {Number(actualAmount) !== 0 ||
                    Number(budgetAmount) !== 0 ||
                    Number(overBudget) !== 0 ? (
                      <>{percentOfBudget.toFixed(1)}%</>
                    ) : null}
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
        ) : null}

        {typeof isOpen[account.name] === "undefined" || isOpen[account.name] ? (
          <>
            {account.children && renderNestedAccounts(account.children, level + 1)}

            {/* TOTALS */}
            {account.isHeader && (
              <tr style={{ backgroundColor: "#f8f9fa", pointerEvents: "none" }}>
                <td className="align-middle">
                  <div
                    style={{
                      paddingLeft: `${level * 20}px`,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <div title={account.name} className="text-truncate ms-4">
                      <b>Total {account.name}</b>
                    </div>
                  </div>
                </td>

                {Array.from({ length: 12 }, (_, monthIndex) => {
                  // Calculate totals for THIS specific month only
                  const monthlyActualAmount = account.children.reduce((childAcc, child) => {
                    return currency(childAcc).add(
                      budgetData?.budget?.actuals[`${child.AccountID}_month_${monthIndex + 1}`] || 0
                    ).value;
                  }, 0);

                  const monthlyBudgetAmount = account.children.reduce((childAcc, child) => {
                    return currency(childAcc).add(
                      budgetData?.budget?.monthlyBudgets[
                        `${child.AccountID}_month_${monthIndex + 1}`
                      ] || 0
                    ).value;
                  }, 0);

                  const monthlyOverBudget = currency(monthlyActualAmount).subtract(
                    monthlyBudgetAmount
                  ).value;

                  const percentOfBudget =
                    monthlyBudgetAmount !== 0
                      ? (monthlyActualAmount / monthlyBudgetAmount) * 100
                      : 0;

                  return (
                    <React.Fragment key={monthIndex}>
                      <td
                        className="align-middle"
                        title={`${account.name} - Actual - ${monthName(monthIndex)}`}
                      >
                        {currency(monthlyActualAmount, {
                          symbol: "",
                        }).format()}
                      </td>
                      <td
                        className="align-middle"
                        title={`${account.name} - Budget - ${monthName(monthIndex)}`}
                      >
                        {currency(monthlyBudgetAmount, {
                          symbol: "",
                        }).format()}
                      </td>
                      <td
                        className="align-middle"
                        title={`${account.name} - Over Budget - ${monthName(monthIndex)}`}
                      >
                        {currency(monthlyOverBudget, {
                          symbol: "",
                        }).format()}
                      </td>
                      <td
                        className="align-middle"
                        title={`${account.name} - % of Budget - ${monthName(monthIndex)}`}
                      >
                        <>{percentOfBudget.toFixed(1)}%</>
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            )}
          </>
        ) : null}
      </React.Fragment>
    ));
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered fullscreen animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {title}: {budget.period}{" "}
          </Modal.Title>

          <Button variant="outline-primary" size="sm" onClick={() => refetch()} className="ms-3">
            {" "}
            <CachedIcon />{" "}
          </Button>

          <Button variant="light-blue" size="sm" onClick={exportToExcel} className="ms-3">
            {" "}
            <ExcelIcon /> Export
          </Button>
        </Modal.Header>
        <Modal.Body scrollable>
          <div
            className="table-responsive"
            style={{ height: "calc(100vh - 6rem)", overflowY: "auto" }}
          >
            <Table striped bordered hover className="sticky-first-col" ref={tableRef}>
              <thead className="text-center">
                <tr>
                  <th />
                  <th colSpan="4">JAN 2025</th>
                  <th colSpan="4">FEB 2025</th>
                  <th colSpan="4">MAR 2025</th>
                  <th colSpan="4">APR 2025</th>
                  <th colSpan="4">MAY 2025</th>
                  <th colSpan="4">JUN 2025</th>
                  <th colSpan="4">JUL 2025</th>
                  <th colSpan="4">AUG 2025</th>
                  <th colSpan="4">SEP 2025</th>
                  <th colSpan="4">OCT 2025</th>
                  <th colSpan="4">NOV 2025</th>
                  <th colSpan="4">DEC 2025</th>
                </tr>
                <tr>
                  <th>Account</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                  <th>ACTUAL</th>
                  <th>BUDGET</th>
                  <th>OVER BUDGET</th>
                  <th>% OF BUDGET</th>
                </tr>
              </thead>

              {/* Render account rows here */}

              <tbody>{renderNestedAccounts(accounts)}</tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BudgetVsActualModal;
